import { useState, useEffect } from 'react';
import { FamLoader } from './components/FamLoader';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { useAuth } from './hooks/useAuth';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { AuthModal } from './components/auth/AuthModal';
import { LandingPage } from './components/landing/LandingPage';
import { GoalPage } from './components/goals/GoalPage';
import { InvestorDashboard } from './components/investor/InvestorDashboard';
import { DealerDashboard } from './components/dealer/DealerDashboard';
import { DealerLandingPage } from './components/dealer/DealerLandingPage';
import { CampaignsPage } from './components/campaigns/CampaignsPage';
import { FunderLandingPage } from './components/landing/FunderLandingPage';
import { FamNews } from './components/blog/FamNews';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { DevJourneyPanel } from './components/dev/DevJourneyPanel';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function FamNewsSlugRoute() {
  const location = useLocation();
  const slug = location.pathname.replace('/news/', '').replace('/news', '') || undefined;
  return <FamNews slug={slug} />;
}

// /dealers — public landing page; CTA opens auth modal, after sign-in user navigates to /dealer
function DealerLandingRoute() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useAuth();

  if (user) {
    window.location.href = '/dealer';
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
      <Header onSignIn={() => setShowAuthModal(true)} />
      <DealerLandingPage onGetStarted={() => setShowAuthModal(true)} />
      <Footer />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

const MIN_LOADER_MS = 600;

// Dealer route: landing page for unauthenticated, dashboard for authenticated
function DealerRoute() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), MIN_LOADER_MS);
    return () => clearTimeout(t);
  }, []);

  if (loading || !minElapsed) {
    return <FamLoader />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
        <Header onSignIn={() => setShowAuthModal(true)} />
        <DealerLandingPage onGetStarted={() => setShowAuthModal(true)} />
        <Footer />
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
      <Header onSignIn={() => {}} />
      <DealerDashboard />
      <Footer />
    </div>
  );
}

// Inner shell that uses Clerk hooks — must be rendered inside <ClerkProvider>
function AppShell() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), MIN_LOADER_MS);
    return () => clearTimeout(t);
  }, []);

  const isCampaignsRoute = location.pathname === '/campaigns';
  const isFunderRoute = location.pathname === '/funder';
  const isNewsRoute = location.pathname.startsWith('/news');
const isAdminRoute = location.pathname === '/admin';
  const isDevRoute = location.pathname === '/dev';
  const isPrivacyRoute = location.pathname === '/privacy';

  if (loading || !minElapsed) {
    return <FamLoader label="Loading FAM..." />;
  }

  const renderContent = () => {
    if (isDevRoute) return <DevJourneyPanel />;
    if (isAdminRoute) return <AdminDashboard />;
    if (isPrivacyRoute) return <PrivacyPolicy />;
    if (isCampaignsRoute) return <CampaignsPage />;
    if (isFunderRoute) return <FunderLandingPage onGetStarted={() => setShowAuthModal(true)} />;
    if (isNewsRoute) {
      const slug = location.pathname.replace('/news/', '').replace('/news', '') || undefined;
      return <FamNews slug={slug || undefined} />;
    }
    if (!user) return <LandingPage onGetStarted={() => setShowAuthModal(true)} />;
    return <InvestorDashboard />;
  };

  if (isDevRoute) {
    return <>{renderContent()}</>;
  }

  if (isAdminRoute) {
    return <>{renderContent()}</>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 transition-opacity duration-200">
      <Header onSignIn={() => setShowAuthModal(true)} />
      {renderContent()}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <Footer />
    </div>
  );
}

export default function App() {
  if (!clerkPublishableKey) {
    // Clerk key is not configured — this should only happen in CI / build preview.
    // Render a minimal shell without auth so the build succeeds and UI is visible.
    console.warn('VITE_CLERK_PUBLISHABLE_KEY is not set. Auth is disabled.');
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/goal/:id" element={<GoalPage />} />
          <Route path="/dealers" element={<DealerLandingPage onGetStarted={() => {}} />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/funder" element={<FunderLandingPage onGetStarted={() => {}} />} />
          <Route path="/news" element={<FamNews />} />
          <Route path="/news/:slug" element={<FamNewsSlugRoute />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/dev" element={<DevJourneyPanel />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="*"
            element={
              <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
                <LandingPage onGetStarted={() => {}} />
              </div>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <Analytics />
      <BrowserRouter>
        <Routes>
          <Route path="/goal/:id" element={<GoalPage />} />
          <Route path="/dealers" element={<DealerLandingRoute />} />
          <Route path="/campaigns" element={<AppShell />} />
          <Route path="/funder" element={<AppShell />} />
          <Route path="/news" element={<AppShell />} />
          <Route path="/news/:slug" element={<AppShell />} />
          <Route path="/investor" element={<AppShell />} />
          <Route path="/dealer" element={<DealerRoute />} />
          <Route path="/admin" element={<AppShell />} />
          <Route path="/dev" element={<AppShell />} />
          <Route path="*" element={<AppShell />} />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
}
